import React from 'react';

import { RatingLetterIcon, NextLink } from 'components';
import { colors, styled } from 'core/styles';
import { Rating, RedirectLinkProps } from 'domain/types';
import { Either } from 'utils/either';
import { RATING_ANALYZE_URL } from 'env';
import { DiagonalArrowIcon } from 'components/icons';

import ratingA from './images/ratingA.webp';
import ratingB from './images/ratingB.webp';
import ratingC from './images/ratingC.webp';
import ratingD from './images/ratingD.webp';
import ratingE from './images/ratingE.webp';

const ratingBg = {
  0: ratingA,
  1: ratingB,
  2: ratingC,
  3: ratingD,
  4: ratingE,
};

export function OverallRatingWidget({
  rating,
  description,
  linkProps,
}: {
  rating: Either<Rating>;
  description: React.ReactNode;
  linkProps?: RedirectLinkProps;
}) {
  return linkProps ? (
    <LinkContainer {...linkProps}>
      <OverallRatingWidgetContent rating={rating} description={description} />
    </LinkContainer>
  ) : (
    <OverallRatingWidgetContent displayNestedLink rating={rating} description={description} />
  );
}

function OverallRatingWidgetContent({
  displayNestedLink = false,
  rating,
  description,
}: {
  displayNestedLink?: boolean;
  rating: Either<Rating>;
  description: React.ReactNode;
}) {
  return (
    <Root
      id="rating-widget-bg"
      bg={rating.fold(
        r => ratingBg[r]?.src,
        () => null,
      )}
    >
      <OverallRatingTop>
        <span>Overall rating</span>
        {displayNestedLink && (
          <Button href={RATING_ANALYZE_URL} target="_blank">
            How do we analyze?
            <DiagonalArrowIcon fontSize="inherit" />
          </Button>
        )}
      </OverallRatingTop>
      <RatingIconBox>
        <RatingLetterIcon rating={rating} />
      </RatingIconBox>
      {description && <RatingDescription>{description}</RatingDescription>}
    </Root>
  );
}

const LinkContainer = styled(NextLink)({
  display: 'block',
  height: '100%',

  '&:hover': {
    '& div[id="rating-widget-bg"]': {
      backgroundSize: '103% 103%',
    },
  },
});

const Root = styled('div', {
  shouldForwardProp: prop => prop !== 'bg',
})<{
  bg: string | null;
}>(({ bg }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
  height: '100%',
  padding: '12px 12px 16px 16px',
  fontSize: 12,
  lineHeight: '16px',
  borderRadius: 16,
  overflow: 'hidden',
  transition: 'background-size 0.25s',
  background: !bg ? colors.smokyWhite : `url(${bg}) 50% / cover no-repeat`,
  backgroundSize: '100% 100%',
  backgroundPosition: 'center center',
}));

const OverallRatingTop = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  lineHeight: '24px',
});

const Button = styled(NextLink)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  height: 24,
  padding: '4px 8px',
  color: colors.white,
  background: colors.grayOpacity[32],
  backdropFilter: 'blur(20px)',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'background 200ms',

  '&:hover': {
    background: colors.grayOpacity[48],
  },
}));

const RatingIconBox = styled('div')({
  fontSize: 74,
  marginTop: 'auto',
});

const RatingDescription = styled('div')(() => ({
  maxWidth: 439,
  color: colors.black,
  fontSize: 24,
  lineHeight: '28px',
  letterSpacing: '-0.25px',
  margin: 0,
}));
